(function () {
  'use strict';

  const DEFAULT_OPEN_TIMEOUT_MS = 20000;
  const DEFAULT_SUBMIT_TIMEOUT_MS = 300000;
  const KLAVIYO_READY_POLL_MS = 250;
  const KLAVIYO_READY_MAX_MS = 25000;
  const OPEN_RETRY_INTERVAL_MS = 800;
  const OPEN_RETRY_COUNT = 12;

  function trimFormId(value) {
    return String(value || '').trim();
  }

  function formIdKey(value) {
    return trimFormId(value).toUpperCase();
  }

  function formIdsMatch(left, right) {
    const a = formIdKey(left);
    const b = formIdKey(right);
    return Boolean(a && b && a === b);
  }

  function storageKey(formId) {
    return `sbb-klaviyo-submitted-${formIdKey(formId)}`;
  }

  function hasSubmittedInSession(formId) {
    try {
      return sessionStorage.getItem(storageKey(formId)) === '1';
    } catch (error) {
      return false;
    }
  }

  function markSubmittedInSession(formId) {
    try {
      sessionStorage.setItem(storageKey(formId), '1');
    } catch (error) {
      // ignore
    }
  }

  function isKlaviyoScriptUrl(value) {
    return /klaviyo/i.test(String(value || ''));
  }

  function releaseAvadaLightScripts() {
    document.querySelectorAll('script[type="lightJs"]').forEach((oldScript) => {
      const src = oldScript.getAttribute('src') || '';
      const text = oldScript.text || '';
      if (!isKlaviyoScriptUrl(src) && !isKlaviyoScriptUrl(text)) {
        return;
      }

      const newScript = document.createElement('script');
      Array.prototype.forEach.call(oldScript.attributes, (attribute) => {
        if (attribute.name !== 'type') {
          newScript.setAttribute(attribute.name, attribute.value);
        }
      });
      newScript.text = oldScript.text;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    ['mousemove', 'touchstart', 'keydown', 'wheel'].forEach((eventName) => {
      try {
        window.dispatchEvent(new Event(eventName, { bubbles: true, cancelable: true }));
      } catch (error) {
        // ignore
      }
    });
  }

  function releaseDelayedThirdPartyScripts() {
    releaseAvadaLightScripts();

    if (typeof window.tepoReleaseDelayedThirdPartyScripts === 'function') {
      window.tepoReleaseDelayedThirdPartyScripts();
      return;
    }

    try {
      window.dispatchEvent(new Event('scroll'));
    } catch (error) {
      // ignore
    }
  }

  function ensureKlaviyoOnsiteQueue() {
    window._klOnsite = window._klOnsite || [];
    return window._klOnsite;
  }

  function isKlaviyoOnsiteReady() {
    ensureKlaviyoOnsiteQueue();
    return (
      typeof window._klOnsite.openForm === 'function' ||
      typeof window.klaviyo?.openForm === 'function'
    );
  }

  function isKlaviyoModalVisible() {
    return Boolean(
      document.querySelector(
        [
          '[data-testid="klaviyo-form"]',
          '[class*="kl-private"]',
          '.needsclick[class*="kl-private"]',
          'div[role="dialog"][class*="kl-"]',
        ].join(','),
      ),
    );
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  async function waitForKlaviyoReady(maxMs) {
    const deadline = Date.now() + Math.max(2000, maxMs || KLAVIYO_READY_MAX_MS);

    while (Date.now() < deadline) {
      releaseDelayedThirdPartyScripts();

      if (isKlaviyoOnsiteReady() || isKlaviyoModalVisible()) {
        return true;
      }

      await wait(KLAVIYO_READY_POLL_MS);
    }

    ensureKlaviyoOnsiteQueue();
    return isKlaviyoOnsiteReady() || isKlaviyoModalVisible();
  }

  function openKlaviyoForm(formId) {
    const normalizedId = trimFormId(formId);
    if (!normalizedId) {
      throw new Error('Missing Klaviyo form ID');
    }

    ensureKlaviyoOnsiteQueue();

    if (typeof window._klOnsite.openForm === 'function') {
      window._klOnsite.openForm(normalizedId);
      return;
    }

    if (typeof window.klaviyo?.openForm === 'function') {
      window.klaviyo.openForm(normalizedId);
      return;
    }

    window._klOnsite.push(['openForm', normalizedId]);
  }

  async function openKlaviyoFormWithRetries(formId) {
    await waitForKlaviyoReady();

    for (let attempt = 0; attempt < OPEN_RETRY_COUNT; attempt += 1) {
      releaseDelayedThirdPartyScripts();
      openKlaviyoForm(formId);

      if (isKlaviyoModalVisible()) {
        return;
      }

      if (attempt < OPEN_RETRY_COUNT - 1) {
        await wait(OPEN_RETRY_INTERVAL_MS);
      }
    }
  }

  function clickCssSelector(selector) {
    const target = document.querySelector(String(selector || '').trim());
    if (!target) {
      throw new Error(`Klaviyo trigger element not found: ${selector}`);
    }
    target.click();
  }

  function runCustomTrigger(customJs, context) {
    const snippet = String(customJs || '').trim();
    if (!snippet) {
      throw new Error('Custom Klaviyo trigger is empty');
    }

    const runner = new Function(
      'gate',
      'formId',
      'openForm',
      'clickSelector',
      `"use strict";\n${snippet}`,
    );

    runner(context, context.formId, context.openForm, context.clickSelector);
  }

  async function triggerPopup(config) {
    const method = String(config.triggerMethod || 'form_id').toLowerCase();
    const formId = trimFormId(config.formId);
    const context = {
      formId,
      openForm: (id) => openKlaviyoForm(id || formId),
      clickSelector: (selector) => clickCssSelector(selector || config.cssSelector),
    };

    await waitForKlaviyoReady();

    if (method === 'css_selector') {
      clickCssSelector(config.cssSelector);
      return;
    }

    if (method === 'custom_js') {
      runCustomTrigger(config.customTriggerJs, context);
      return;
    }

    await openKlaviyoFormWithRetries(formId);
  }

  function isSuccessfulSubmissionEvent(type) {
    return (
      type === 'submit' ||
      type === 'redirectedToUrl' ||
      type === 'stepSubmit' ||
      type === 'stepSubmitSuccess'
    );
  }

  function waitForKlaviyoSubmission(config) {
    const formId = trimFormId(config.formId);
    const openTimeoutMs = Math.max(3000, Number(config.openTimeoutMs) || DEFAULT_OPEN_TIMEOUT_MS);
    const submitTimeoutMs = Math.max(
      openTimeoutMs + 1000,
      Number(config.submitTimeoutMs) || DEFAULT_SUBMIT_TIMEOUT_MS,
    );
    const revealDelayMs = Math.max(0, Number(config.revealDelayMs) || 0);

    return new Promise((resolve, reject) => {
      let settled = false;
      let opened = false;
      let submitted = false;
      let openTimer = null;
      let submitTimer = null;
      let domObserver = null;

      function cleanup() {
        window.removeEventListener('klaviyoForms', onKlaviyoForms);
        if (openTimer) window.clearTimeout(openTimer);
        if (submitTimer) window.clearTimeout(submitTimer);
        if (domObserver) domObserver.disconnect();
      }

      function markOpened() {
        if (opened) return;
        opened = true;
        document.dispatchEvent(new CustomEvent('sbb:klaviyo-opened'));
        if (openTimer) {
          window.clearTimeout(openTimer);
          openTimer = null;
        }
      }

      function finish(result) {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      }

      function fail(error) {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      }

      async function onSubmit() {
        markSubmittedInSession(formId);
        if (revealDelayMs > 0) {
          await wait(revealDelayMs);
        }
        finish('submitted');
      }

      function onKlaviyoForms(event) {
        const detail = event?.detail;
        if (!detail) return;

        if (formId && detail.formId && !formIdsMatch(detail.formId, formId)) {
          return;
        }

        if (detail.type === 'open' || detail.type === 'embedOpen') {
          markOpened();
          return;
        }

        if (detail.type === 'close') {
          if (!opened || submitted) return;
          fail(new Error('Klaviyo popup closed before submission'));
          return;
        }

        if (isSuccessfulSubmissionEvent(detail.type)) {
          submitted = true;
          onSubmit();
        }
      }

      domObserver = new MutationObserver(() => {
        if (isKlaviyoModalVisible()) {
          markOpened();
        }
      });
      domObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });

      window.addEventListener('klaviyoForms', onKlaviyoForms);

      openTimer = window.setTimeout(() => {
        if (!opened && isKlaviyoModalVisible()) {
          markOpened();
          return;
        }
        if (!opened && config.fallbackOnFailure) {
          finish('fallback');
          return;
        }
        if (!opened) {
          fail(
            new Error(
              'Klaviyo popup did not open. In Klaviyo, set Display timing to “Only show on custom trigger”.',
            ),
          );
        }
      }, openTimeoutMs);

      submitTimer = window.setTimeout(() => {
        if (config.fallbackOnFailure) {
          finish('fallback');
          return;
        }
        fail(new Error('Klaviyo form submission timed out'));
      }, submitTimeoutMs);

      triggerPopup(config).catch((error) => {
        if (config.fallbackOnFailure) {
          finish('fallback');
          return;
        }
        fail(error);
      });
    });
  }

  async function waitForSubmission(config) {
    if (!config?.enabled) {
      return 'skipped';
    }

    const formId = trimFormId(config.formId);
    const method = String(config.triggerMethod || 'form_id').toLowerCase();

    if (method === 'form_id' && !formId) {
      if (config.fallbackOnFailure) return 'fallback';
      throw new Error('Klaviyo form ID is required');
    }

    if (method === 'css_selector' && !String(config.cssSelector || '').trim()) {
      if (config.fallbackOnFailure) return 'fallback';
      throw new Error('CSS selector is required');
    }

    if (config.skipIfAlreadySubmitted && formId && hasSubmittedInSession(formId)) {
      if (config.reopenForCodeCopy !== false) {
        releaseDelayedThirdPartyScripts();
        await waitForKlaviyoReady();
        await triggerPopup(config);
        return 'reopened';
      }
      return 'submitted';
    }

    releaseDelayedThirdPartyScripts();
    await waitForKlaviyoReady();

    return waitForKlaviyoSubmission(config);
  }

  async function preload() {
    releaseDelayedThirdPartyScripts();
    await waitForKlaviyoReady();
  }

  async function openPopup(config) {
    if (!config?.enabled) {
      return false;
    }

    releaseDelayedThirdPartyScripts();
    await waitForKlaviyoReady();
    await triggerPopup(config);
    return isKlaviyoModalVisible();
  }

  window.SbbKlaviyoGate = {
    waitForSubmission,
    preload,
    openPopup,
    releaseDelayedThirdPartyScripts,
    releaseAvadaLightScripts,
  };
})();
